import { time } from "@dinahmoe/dmafjs-core";
import { assetDictionary } from "@dinahmoe/dmafjs-assetloader";
import { webAudioUtils } from "./webaudio_utils";
import { webAudioContext } from "./webaudio_context";
import { NodeHtml } from "./node_html";

class Source {
  constructor({ assetId, playbackRate, WebAudioContext, AssetDictionary }) {
    this.source = WebAudioContext.context.createBufferSource();
    this.source.buffer = AssetDictionary.get("assets.buffer." + assetId);
    this.source.playbackRate.value = playbackRate;
  }
}

class SoundHtml extends NodeHtml {
  handlers = this.extendParentHandlers({
    volume: { handler: this.setVolume, type: "float", default: 0, min: -80, max: 12 },
    play: this.setPlay,
    stop: this.setStop,
  });

  setProps(dnode) {
    const output = webAudioContext.context.createGain();
    output.gain.value = webAudioUtils.dbToJs(dnode.props.volume);
    dnode.output = output;
    dnode.scheduledStops = new Set();
    dnode.soundsPlaying = [];
    super.setProps(dnode);
  }

  setVolume(dnode) {
    dnode.output.gain.value = webAudioUtils.dbToJs(dnode.props.volume);
  }

  setPlay(dnode) {
    const { play, assetId, playbackRate, ignoreEventTime } = dnode.props;

    const startTime = ignoreEventTime ? webAudioContext.context.currentTime : play.waTime / 1000;
    const fadeIn = dnode.props.fadeIn / 1000;

    const sourceObj = new Source({
      assetId,
      playbackRate,
      WebAudioContext: webAudioContext,
      AssetDictionary: assetDictionary,
    });
    sourceObj.source.connect(dnode.output);

    const gainValue = dnode.output.gain.value;

    let startGain = fadeIn > 0 ? 0 : gainValue;

    if (dnode.soundsPlaying.length > 0) {
      dnode.scheduledStops.forEach((stop) => clearTimeout(stop));
      dnode.scheduledStops.clear();
      // if sound is playing: fade in from current value
      startGain = dnode.output.gain.value;
      dnode.output.gain.cancelScheduledValues(startTime);
    }

    dnode.output.gain.setValueAtTime(startGain, startTime);
    dnode.output.gain.linearRampToValueAtTime(
      webAudioUtils.dbToJs(dnode.props.volume),
      startTime + fadeIn,
    );

    if (ignoreEventTime) {
      sourceObj.source.start();
    } else if (webAudioContext.context.currentTime > startTime) {
      const startPosition = webAudioContext.context.currentTime - startTime;
      sourceObj.source.start(0, startPosition);
    } else {
      sourceObj.source.start(startTime);
    }

    dnode.soundsPlaying.push(sourceObj);

    const onEnd = () => {
      dnode.soundsPlaying = dnode.soundsPlaying.filter(
        (soundPlaying) => soundPlaying.source !== sourceObj.source,
      );
      sourceObj.source.removeEventListener("ended", onEnd);
    }
    sourceObj.source.addEventListener("ended", onEnd);
  }

  setStop(dnode) {
    const { stop } = dnode.props;

    const stopTime = stop.waTime / 1000;
    const previousGain = dnode.output.gain.value;
    const fadeOut = stop.fadeOut / 1000;

    if (fadeOut === 0) {
      this.#stopPlayingSounds(dnode);
      dnode.soundsPlaying = [];
      return;
    }

    dnode.output.gain.setValueAtTime(previousGain, stopTime);
    dnode.output.gain.linearRampToValueAtTime(0, stopTime + fadeOut);

    const delay = (stopTime + fadeOut) - webAudioContext.context.currentTime
    const scheduledStop = setTimeout(() => {
      this.#stopPlayingSounds(dnode);
      dnode.output.gain.setValueAtTime(previousGain, stopTime + fadeOut);
    }, delay * 1000);
    
    dnode.scheduledStops.add(scheduledStop);
  }

  #stopPlayingSounds(dnode) {
    const { soundsPlaying } = dnode;
    soundsPlaying.forEach((sound) => {
      sound.source.stop();
    });
  }
}

const soundHtml = new SoundHtml("webaudio:sound");
export { SoundHtml, soundHtml };
